import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Dropzone } from '../../components/project/Dropzone/Dropzone';
import { downloadReport } from '../../entities/project/api/export';
import { ProjectHeader } from '../../components/project/ProjectHeader/ProjectHeader';
import { ProjectSettingsBar } from '../../components/project/ProjectSettingsBar/ProjectSettingsBar';
import { PositionsPanel } from '../../components/project/PositionsPanel/PositionsPanel';
import { DeletePositionModal } from '../../components/project/DeletePositionModal/DeletePositionModal';
import { RestartSearchModal } from '../../components/project/RestartSearchModal/RestartSearchModal';
import { DimensionsModal } from '../../components/project/DimensionsModal/DimensionsModal';
import {
    ManualPositionsModal,
    emptyDraft,
} from '../../components/project/ManualPositionsModal/ManualPositionsModal';
import { useToast } from '../../components/ui/Toast/ToastProvider';

import { useAppSelector } from '../../app/hooks';
import { selectOrganizationId } from '../../entities/organization/model/slice';
import { selectUser } from '../../entities/session/model/slice';
import {
    useGetProjectsQuery,
    useRestartProjectMutation,
    useStopSearchMutation,
    useUpdateProjectMutation,
    useFixDimensionsMutation,
    useMakeScreenshotsMutation,
} from '../../entities/project/api/projectApi';
import { downloadPositionsTemplate } from '../../entities/project/api/template';

import { downloadScreenshots } from '../../entities/project/api/screenshots';
import { screenshotsStateFor } from '../../entities/project/model/screenshots';

import { useGetRequestLimitQuery } from '../../entities/limits/api/limitsApi';
import { isAdmin } from '../../shared/lib/access';
import { useProjectPositions } from '../../hooks/useProjectPositions';
import type { Position, PositionDraft, ProjectStatus } from '../../types/project';
import { DEFAULT_REGION } from '../../data/projectCard';
import { pluralWithCount } from '../../utils/format';
import { IconSpinner } from '../../assets/icons/icons';
import styles from './ProjectCardPage.module.scss';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Loading } from '../../components/ui/Loading/Loading';

const ACTION_COOLDOWN_MS = 5000;
const OPTIMISTIC_STATUS_TTL_MS = 60000;

const BRANCHES_WAIT_MESSAGE = 'Дождитесь окончания поиска — ещё ищем аналоги и предложения площадок';

interface ProjectCardPageProps {
    onBack?: () => void;
}

export const ProjectCardPage: FC<ProjectCardPageProps> = ({ onBack }) => {
    const notify = useToast();
    const navigate = useNavigate();
    const { projectId } = useParams<{ projectId: string }>();

    const positionsApi = useProjectPositions(projectId ?? '');

    const {
        positions,
        reportId,
        progress,
        isDeliveryProcessing,
        delivery,
        logisticsCount,
        deliveryAddress,
        pendingCount,
        isStartingSearch,
        isSearching,
        setSearchRunning,
        handleFile,
        runSearch,
        setFlagAll,
        dirtyIds,
        bulkDirtyIds,
        restartPosition,
        discardEdits,
    } = positionsApi;

    const [stopSearch] = useStopSearchMutation();
    const [updateProject] = useUpdateProjectMutation();
    const [restartProject, { isLoading: isRestarting }] = useRestartProjectMutation();

    const [title, setTitle] = useState('');
    const [status, setStatus] = useState<ProjectStatus | null>(null);

    const optimisticStatus = useRef<ProjectStatus | null>(null);
    const optimisticTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [dimensionsId, setDimensionsId] = useState<string | null>(null);

    const [fixDimensions, { isLoading: isSavingDimensions }] = useFixDimensionsMutation();

    const [editingId, setEditingId] = useState<string | null>(null);
    const [region, setRegion] = useState(DEFAULT_REGION);

    const [manualSeed, setManualSeed] = useState<PositionDraft | null>(null);
    const [manualOpen, setManualOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Position | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [restartOpen, setRestartOpen] = useState(false);

    const [actionCooldown, setActionCooldown] = useState(false);
    const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [stopping, setStopping] = useState(false);

    const [restartingDirty, setRestartingDirty] = useState(false);

    useEffect(
        () => () => {
            if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
            if (optimisticTimer.current) clearTimeout(optimisticTimer.current);
        },
        [],
    );

    const startCooldown = useCallback(() => {
        setActionCooldown(true);
        if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => setActionCooldown(false), ACTION_COOLDOWN_MS);
    }, []);

    const setStatusOptimistic = useCallback((next: ProjectStatus) => {
        optimisticStatus.current = next;
        setStatus(next);

        if (optimisticTimer.current) clearTimeout(optimisticTimer.current);
        optimisticTimer.current = setTimeout(() => {
            optimisticStatus.current = null;
        }, OPTIMISTIC_STATUS_TTL_MS);
    }, []);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const organizationId = useAppSelector(selectOrganizationId);

    const user = useAppSelector(selectUser);

    const { data: limit } = useGetRequestLimitQuery(
        { organizationId, admin: isAdmin(user) },
        { skip: !organizationId },
    );

    const [makeScreenshots, { isLoading: isMakingScreenshots }] = useMakeScreenshotsMutation();
    const [isDownloadingScreenshots, setDownloadingScreenshots] = useState(false);

    const [remakeOpen, setRemakeOpen] = useState(false);

    const [clearOpen, setClearOpen] = useState(false);

    const clearAll = useCallback(() => {
        positionsApi.clearDrafts();
        setClearOpen(false);
        notify('Список позиций очищен');
    }, [positionsApi, notify]);

    const screenshots = screenshotsStateFor(positionsApi.reportStatus, positionsApi.screenshots);

    const startScreenshots = useCallback(async () => {
        if (!projectId) return;
        setRemakeOpen(false);
        try {
            await makeScreenshots(projectId).unwrap();
            notify('Создаём скриншоты — на большом проекте это займёт несколько минут');
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [projectId, makeScreenshots, notify]);

    const handleScreenshots = useCallback(async () => {
        if (!projectId) return;

        switch (screenshots.action) {
            case 'download':
                setDownloadingScreenshots(true);
                try {
                    await downloadScreenshots(projectId, title);
                } catch (error) {
                    notify(error instanceof Error ? error.message : 'Не удалось скачать скриншоты');
                } finally {
                    setDownloadingScreenshots(false);
                }
                break;
            case 'remake':
                setRemakeOpen(true);
                break;
            case 'make':
                await startScreenshots();
                break;
            default:
                break;
        }
    }, [projectId, screenshots.action, title, startScreenshots, notify]);

    const branchesLeft = positionsApi.branchesLeft;

    const { project } = useGetProjectsQuery(
        { organizationId },
        {
            skip: !organizationId,
            pollingInterval: status === 'run' || isSearching || branchesLeft > 0 ? 5000 : 0,
            selectFromResult: ({ data }) => ({
                project: data?.find((item) => item.id === projectId),
            }),
        },
    );

    useEffect(() => {
        if (!project) return;

        setTitle(project.name);

        if (optimisticStatus.current) {
            if (project.status !== optimisticStatus.current) return;

            optimisticStatus.current = null;
            if (optimisticTimer.current) clearTimeout(optimisticTimer.current);
        }

        setStatus(project.status);
    }, [project]);

    const isBranches = Boolean(project?.isBranches) || branchesLeft > 0;

    /* ─── навигация ─── */

    const handleBack = useCallback(() => {
        if (onBack) onBack();
        else navigate('/projects');
    }, [onBack, navigate]);

    /* ─── шапка ─── */

    const handleRename = async (next: string): Promise<boolean> => {
        if (!next || !projectId) {
            notify('Наименование не может быть пустым');
            return false;
        }

        try {
            await updateProject({ id: projectId, body: { name: next } }).unwrap();
            setTitle(next);
            document.title = `Предиктивная аналитика ЯНАО — ${next}`;
            notify('Наименование проекта сохранено');
            return true;
        } catch {
            return false;
        }
    };

    const downloadTemplate = async (): Promise<void> => {
        try {
            await downloadPositionsTemplate();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Не удалось скачать шаблон');
        }
    };

    /* ─── загрузка файла ─── */

    const pickFile = () => fileInputRef.current?.click();

    const acceptFile = useCallback(
        async (file: File | null) => {
            const message = await handleFile(file);
            if (message) notify(message);
        },
        [handleFile, notify],
    );

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        if (file) acceptFile(file);
        event.target.value = '';
    };

    /* ─── добавление позиций ─── */

    const openManual = (seed: PositionDraft | null) => {
        setManualSeed(seed);
        setManualOpen(true);
    };

    const closeManual = () => {
        setManualOpen(false);
        setManualSeed(null);
        setEditingId(null);
    };

    const handleManualSubmit = async (drafts: PositionDraft[]) => {
        if (editingId) {
            const [draft] = drafts;
            if (!draft) return;

            if (editingId.startsWith('local:')) {
                await positionsApi.updatePosition(editingId, draft);
                notify('Позиция изменена');
                closeManual();
                return;
            }

            const current = positions.find((item) => item.id === editingId);

            const usable = (current?.offers ?? []).filter(
                (offer) => offer.dimensions && !offer.dimensions.failed && offer.dimensions.length,
            );

            const baseOffer =
                usable.find((offer) => offer.dimensions?.origin === 'user_data') ?? usable[0];
            const base = baseOffer?.dimensions;

            const dims = {
                length: draft.length ?? 0,
                width: draft.width ?? 0,
                height: draft.height ?? 0,
                weight: Math.round(draft.weight ?? 0),
            };

            const ownDims = current?.dimensions;
            const hasOwnDims = Boolean(
                ownDims && (ownDims.length || ownDims.width || ownDims.height || ownDims.weight),
            );
            const shownDims = hasOwnDims ? ownDims : base;

            const dimsChanged =
                Boolean(shownDims) &&
                (dims.length !== (shownDims?.length ?? 0) ||
                    dims.width !== (shownDims?.width ?? 0) ||
                    dims.height !== (shownDims?.height ?? 0) ||
                    dims.weight !== Math.round(shownDims?.weight ?? 0));

            const tr = {
                length: draft.transportLength ?? 0,
                width: draft.transportWidth ?? 0,
                height: draft.transportHeight ?? 0,
                weight: Math.round(draft.transportWeight ?? 0),
            };

            const trCurrent = current?.transport;
            const trBase = trCurrent && !trCurrent.isEmpty ? trCurrent : shownDims;

            const transportChanged =
                tr.length !== (trBase?.length ?? 0) ||
                tr.width !== (trBase?.width ?? 0) ||
                tr.height !== (trBase?.height ?? 0) ||
                tr.weight !== Math.round(trBase?.weight ?? 0);

            const searchChanged =
                draft.name !== current?.name ||
                draft.spec !== current?.spec ||
                draft.region !== current?.region ||
                draft.priceFrom !== current?.priceFrom ||
                draft.priceTo !== current?.priceTo ||
                draft.analogs !== current?.analogs ||
                draft.logistics !== current?.logistics ||
                draft.qty !== current?.qty ||
                draft.unit !== current?.unit ||
                (draft.requestPrice ?? null) !== (current?.requestPrice ?? null);

            if ((dimsChanged || transportChanged) && !searchChanged) {
                if (!base) {
                    notify('У позиции нет предложений с габаритами — править нечего');
                    return;
                }

                const outOfRange = (v: {
                    length: number;
                    width: number;
                    height: number;
                    weight: number;
                }) =>
                    [v.length, v.width, v.height].some((mm) => mm <= 0 || mm > 100000) ||
                    v.weight <= 0 ||
                    v.weight > 50000000;

                if (dimsChanged && outOfRange(dims)) {
                    notify('Размеры — от 1 до 100 000 мм, вес — от 1 г до 50 тонн');
                    return;
                }

                if (transportChanged && outOfRange(tr)) {
                    notify('Транспортные габариты заполняются целиком: три размера и вес');
                    return;
                }

                try {
                    await fixDimensions({
                        project_id: projectId ?? '',
                        organization_id: organizationId,
                        product_id: editingId,
                        name: current?.name,
                        url: baseOffer?.url ?? '',
                        weight: Math.round(shownDims?.weight ?? 0),
                        length: shownDims?.length ?? 0,
                        width: shownDims?.width ?? 0,
                        height: shownDims?.height ?? 0,
                        correct_weight: dimsChanged
                            ? dims.weight
                            : Math.round(shownDims?.weight ?? 0),
                        correct_length: dimsChanged ? dims.length : shownDims?.length ?? 0,
                        correct_width: dimsChanged ? dims.width : shownDims?.width ?? 0,
                        correct_height: dimsChanged ? dims.height : shownDims?.height ?? 0,
                        ...(transportChanged
                            ? {
                                transport_length: tr.length,
                                transport_width: tr.width,
                                transport_height: tr.height,
                                transport_weight: tr.weight,
                            }
                            : {}),
                    }).unwrap();
                } catch {
                    return;
                }

                positionsApi.setSearchRunning(true);

                notify('Габариты сохранены, доставка пересчитывается');
                closeManual();
                return;
            }

            const restarted = await positionsApi.restartPosition(editingId, draft);
            if (!restarted) return;

            notify('Поиск по позиции запущен заново');
            closeManual();
            return;
        }

        await positionsApi.addPositions(drafts);
        notify(
            drafts.length === 1
                ? 'Позиция добавлена в заявку'
                : `Добавлено позиций: ${drafts.length}`,
        );
        closeManual();
    };

    /* ─── удаление позиции ─── */

    const confirmDelete = async () => {
        if (!deleteTarget || deleting) return;

        setDeleting(true);
        try {
            await positionsApi.removePosition(deleteTarget.id);
            notify(`Позиция «${deleteTarget.name}» удалена`);
            setDeleteTarget(null);
        } catch {
            /* модалка остаётся открытой — можно повторить */
        } finally {
            setDeleting(false);
        }
    };

    const cancelDelete = () => {
        if (deleting) return;
        setDeleteTarget(null);
    };

    /* ─── действия по позициям ─── */

    const handleToggle = async (id: string, key: 'analogs' | 'logistics') => {
        const position = positions.find((item) => item.id === id);
        if (!position) return;
        await positionsApi.toggleFlag(id, key);

        const { label, off, on } =
            key === 'analogs'
                ? { label: 'Аналоги', off: 'отключены', on: 'включены' }
                : { label: 'Логистика', off: 'отключена', on: 'включена' };

        const state = position[key] ? off : on;

        notify(
            id.startsWith('local:')
                ? `${label} ${state} для позиции «${position.name}»`
                : `${label} ${state} — нажмите «Перезапустить поиск», чтобы применить`,
        );
    };

    const handleToggleAll = useCallback(
        (key: 'analogs' | 'logistics', value: boolean) => {
            const hasSaved = positions.some(
                (item) => !item.id.startsWith('local:') && Boolean(item[key]) !== value,
            );

            const changed = setFlagAll(key, value);
            if (!changed) return;

            const { label, off, on } =
                key === 'analogs'
                    ? { label: 'Аналоги', off: 'отключены', on: 'включены' }
                    : { label: 'Логистика', off: 'отключена', on: 'включена' };

            const count = pluralWithCount(changed, 'позиции', 'позиций', 'позиций');

            notify(
                hasSaved
                    ? `${label} ${value ? on : off} для ${count} — нажмите «Перезапустить изменённые», чтобы применить`
                    : `${label} ${value ? on : off} для ${count}`,
            );
        },
        [positions, setFlagAll, notify],
    );

    const [offerDirtyIds, setOfferDirtyIds] = useState<Set<string>>(() => new Set());

    const allDirtyIds = useMemo(
        () =>
            offerDirtyIds.size
                ? new Set(Array.from(dirtyIds).concat(Array.from(offerDirtyIds)))
                : dirtyIds,
        [dirtyIds, offerDirtyIds],
    );

    const handleOfferDeleted = useCallback((id: string) => {
        setOfferDirtyIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    }, []);

    const clearOfferDirty = useCallback((id: string) => {
        setOfferDirtyIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const handleRestartPosition = useCallback(
        async (id: string) => {
            const ok = await restartPosition(id);
            if (ok) clearOfferDirty(id);
            return ok;
        },
        [restartPosition, clearOfferDirty],
    );

    /* удалённую ссылку не вернуть — «отмена» просто убирает плашку */
    const handleDiscardPosition = useCallback(
        (id: string) => {
            clearOfferDirty(id);
            if (dirtyIds.has(id)) void discardEdits(id);
        },
        [dirtyIds, discardEdits, clearOfferDirty],
    );

    /* ─── общие кнопки для изменённых позиций ─── */

    const handleRestartDirty = useCallback(async () => {
        const ids = Array.from(allDirtyIds);
        if (!ids.length) return;

        setRestartingDirty(true);
        let ok = 0;

        try {
            // по очереди, чтобы не завалить сервер параллельными запросами
            for (const id of ids) {
                if (await handleRestartPosition(id)) ok += 1;
            }
        } finally {
            setRestartingDirty(false);
        }

        if (ok) {
            notify(`Поиск перезапущен по ${pluralWithCount(ok, 'позиции', 'позициям', 'позициям')}`);
        }
        if (ok < ids.length) {
            notify(`Не удалось перезапустить позиций: ${ids.length - ok}`);
        }
    }, [allDirtyIds, handleRestartPosition, notify]);

    const handleDiscardDirty = useCallback(() => {
        Array.from(allDirtyIds).forEach((id) => handleDiscardPosition(id));
        notify('Изменения отменены');
    }, [allDirtyIds, handleDiscardPosition, notify]);

    const handleEdit = (position: Position) => {
        setEditingId(position.id);

        const usable = (position.offers ?? []).filter(
            (offer) => offer.dimensions && !offer.dimensions.failed && offer.dimensions.length,
        );

        const found = (
            usable.find((offer) => offer.dimensions?.origin === 'user_data') ?? usable[0]
        )?.dimensions;
        const own = position.dimensions;
        const hasOwn = Boolean(own && (own.length || own.width || own.height || own.weight));
        const base = hasOwn ? own : found;

        const trOwn = position.transport;
        const tr = trOwn && !trOwn.isEmpty ? trOwn : base;

        openManual({
            ...position,
            length: base?.length ?? undefined,
            width: base?.width ?? undefined,
            height: base?.height ?? undefined,
            weight: base?.weight ?? undefined,
            transportLength: tr?.length ?? undefined,
            transportWidth: tr?.width ?? undefined,
            transportHeight: tr?.height ?? undefined,
            transportWeight: tr?.weight ?? undefined,
        });
    };

    const handleRunSearch = useCallback(async () => {
        if (logisticsCount > 0 && !deliveryAddress) {
            notify('Укажите адрес доставки — по заявке включена логистика');
            return;
        }

        if (limit?.isReached) {
            notify('Лимит запросов организации исчерпан — поиск недоступен до обновления лимита');
            return;
        }

        if (limit && pendingCount > limit.remaining) {
            notify(
                `Осталось запросов: ${limit.remaining}, а позиций к обработке ${pendingCount}. Уберите лишние или дождитесь обновления лимита`,
            );
            return;
        }

        const result = await runSearch(region);

        if (typeof result === 'string') {
            // Пустая строка — ошибку уже показал notifyOnError
            if (result) notify(result);
            return;
        }

        startCooldown();
        setStatusOptimistic('run');
        notify(
            result.duplicates
                ? `Поиск запущен по ${result.started} поз. · дубли пропущены: ${result.duplicates}`
                : `Поиск запущен по ${pluralWithCount(result.started, 'позиции', 'позициям', 'позициям')}`,
        );
    }, [
        logisticsCount,
        deliveryAddress,
        limit,
        pendingCount,
        region,
        runSearch,
        startCooldown,
        setStatusOptimistic,
        notify,
    ]);

    const handleStopSearch = useCallback(async () => {
        if (!projectId) return;
        startCooldown();

        try {
            await stopSearch(projectId).unwrap();
            setStopping(true);
            notify('Останавливаем поиск…');
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [projectId, stopSearch, startCooldown, notify]);

    useEffect(() => {
        if (!stopping) return;
        if (project && project.status !== 'run') {
            setStopping(false);
            setSearchRunning(false);
            notify('Поиск проекта остановлен');
        }
    }, [stopping, project, setSearchRunning, notify]);

    const handleRestartConfirm = useCallback(async () => {
        if (!projectId) return;
        if (isBranches) {
            notify(BRANCHES_WAIT_MESSAGE);
            return;
        }
        if (stopping || project?.status === 'run') {
            notify('Поиск ещё останавливается — подождите несколько секунд');
            return;
        }
        startCooldown();

        try {
            await restartProject(projectId).unwrap();
            setRestartOpen(false);
            setOfferDirtyIds(new Set());
            setSearchRunning(true);
            setStatusOptimistic('run');
            notify('Поиск проекта перезапущен');
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [
        projectId,
        isBranches,
        stopping,
        project?.status,
        restartProject,
        setSearchRunning,
        startCooldown,
        setStatusOptimistic,
        notify,
    ]);

    return (
        <>
            <div className={styles.work}>
                <ProjectHeader
                    title={title}
                    status={isBranches ? 'run' : status}
                    branches={isBranches ? { left: branchesLeft, total: positionsApi.total } : null}
                    createdAt={project?.date}
                    author={project?.author}
                    projectId={projectId ?? ''}
                    onRename={handleRename}
                    onDownloadTemplate={downloadTemplate}
                />

                <ProjectSettingsBar
                    region={region}
                    onRegionChange={setRegion}
                    logisticsCount={logisticsCount}
                    address={deliveryAddress}
                    isDeliveryProcessing={isDeliveryProcessing}
                    deliveryPending={delivery?.pendingProducts}
                    onAddressChange={positionsApi.setDeliveryAddress}
                    onAddressCommit={async () => {
                        const saved = await positionsApi.commitDeliveryAddress();
                        if (saved) notify('Адрес доставки сохранён');
                    }}
                />

                {positionsApi.uiState === 'fetching' ? (
                    <Loading text="Загружаем позиции проекта…" size="md" />
                ) : positions.length === 0 ||
                    positionsApi.uiState === 'loading' ||
                    positionsApi.uiState === 'success' ? (
                    <Dropzone
                        state={
                            positionsApi.uiState === 'loading' || positionsApi.uiState === 'success'
                                ? positionsApi.uiState
                                : 'empty'
                        }
                        fileName={positionsApi.lastFileName}
                        onPickFile={pickFile}
                        onDropFile={acceptFile}
                        onAddManually={() => openManual(emptyDraft())}
                    />
                ) : (
                    <PositionsPanel
                        reportId={reportId}
                        positions={positions}
                        page={positionsApi.page}
                        pageCount={positionsApi.pageCount}
                        total={positionsApi.total}
                        isPageLoading={positionsApi.isPageLoading}
                        onPageChange={positionsApi.setPage}
                        deliveryAvgTotal={positionsApi.deliveryAvgTotal}
                        logisticsCount={logisticsCount}
                        deliveryAddress={deliveryAddress}
                        delivery={delivery}
                        pendingCount={pendingCount}
                        searchStarting={isStartingSearch}
                        onPickFile={pickFile}
                        onAddManually={() => openManual(emptyDraft())}
                        projectRegion={region}
                        dirtyIds={allDirtyIds}
                        bulkDirtyIds={bulkDirtyIds}
                        onRestartPosition={(id) => void handleRestartPosition(id)}
                        onDiscardPosition={handleDiscardPosition}
                        onOfferDeleted={handleOfferDeleted}
                        onRestartDirty={() => void handleRestartDirty()}
                        onDiscardDirty={handleDiscardDirty}
                        restartingDirty={restartingDirty}
                        onShowDimensions={setDimensionsId}
                        onChangePosition={positionsApi.updatePosition}
                        onEditPosition={handleEdit}
                        onDeletePosition={setDeleteTarget}
                        onRunSearch={handleRunSearch}
                        onClearAll={() => setClearOpen(true)}
                        limitLeft={limit ? limit.remaining : null}
                        onRestartSearch={() => {
                            if (isBranches) {
                                notify(BRANCHES_WAIT_MESSAGE);
                            } else if (stopping || project?.status === 'run') {
                                notify('Поиск ещё останавливается — подождите несколько секунд');
                            } else {
                                setRestartOpen(true);
                            }
                        }}
                        isSearching={isSearching}
                        stopping={stopping}
                        actionBusy={actionCooldown}
                        onStopSearch={() => void handleStopSearch()}
                        onDownload={async () => {
                            if (!reportId) return;
                            await downloadReport(reportId, title, logisticsCount > 0);
                        }}
                        isCompleted={(status === 'done' || isBranches) && positionsApi.isReportFinished}
                        screenshots={screenshots}
                        screenshotsBusy={isMakingScreenshots || isDownloadingScreenshots}
                        onScreenshots={() => void handleScreenshots()}
                        branchesBusy={isBranches}
                        onTogglePosition={handleToggle}
                        onToggleAll={handleToggleAll}
                    />
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={onFileInputChange}
            />

            <DimensionsModal
                projectId={projectId ?? ''}
                position={positions.find((item) => item.id === dimensionsId) ?? null}
                onEdit={() => {
                    const target = positions.find((item) => item.id === dimensionsId);

                    setDimensionsId(null);
                    if (target) handleEdit(target);
                }}
                onClose={() => setDimensionsId(null)}
            />

            <ManualPositionsModal
                open={manualOpen}
                editing={Boolean(editingId)}
                submitAsRestart={Boolean(editingId) && !editingId?.startsWith('local:')}
                busy={isSavingDimensions}
                projectRegion={region}
                seed={manualSeed}
                onClose={closeManual}
                onSubmit={handleManualSubmit}
                onValidationError={notify}
            />

            <DeletePositionModal
                open={Boolean(deleteTarget)}
                positionName={deleteTarget?.name}
                busy={deleting}
                onCancel={cancelDelete}
                onConfirm={() => void confirmDelete()}
            />

            <RestartSearchModal
                open={restartOpen}
                submitting={isRestarting}
                onCancel={() => setRestartOpen(false)}
                onConfirm={handleRestartConfirm}
            />

            <ConfirmModal
                open={remakeOpen}
                title="Переснять скриншоты?"
                description="Все текущие скриншоты будут удалены и созданы заново. На большом проекте это займёт несколько минут."
                confirmLabel="Переснять"
                busy={isMakingScreenshots}
                onConfirm={startScreenshots}
                onClose={() => setRemakeOpen(false)}
            />

            <ConfirmModal
                open={clearOpen}
                title="Очистить список позиций?"
                description="Все позиции будут удалены из заявки, включая загруженные из файла. Восстановить их можно только повторной загрузкой."
                confirmLabel="Очистить"
                onConfirm={clearAll}
                onClose={() => setClearOpen(false)}
            />

            <span className={styles.srOnly} aria-live="polite">
                {pluralWithCount(positions.length, 'позиция', 'позиции', 'позиций')} в заявке
            </span>
        </>
    );
};